if (!customElements.get('bedly-bundle-selector')) {
  customElements.define(
    'bedly-bundle-selector',
    class BedlyBundleSelector extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        requestAnimationFrame(() => {
          if (!this.isConnected) return;

          this.quantityInput = document.getElementById(`Quantity-${this.dataset.sectionId}`);
          this.radios = Array.from(this.querySelectorAll('[data-bedly-bundle-quantity]'));
          this.itemRows = Array.from(this.querySelectorAll('[data-bedly-bundle-item]'));
          this.productInfo = this.closest('product-info');
          this.productForm = this.productInfo?.querySelector('product-form');
          this.form = this.productForm?.querySelector('form');
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.errorElement = this.querySelector('[data-bedly-bundle-error]');
          this.variants = this.parseVariants();
          this.handleFormSubmitBound = this.handleFormSubmit.bind(this);
          this.handleBuyNowClickBound = this.handleBuyNowClick.bind(this);

          this.addEventListener('change', (event) => {
            if (event.target.matches('[data-bedly-bundle-quantity]')) {
              const quantity = Number(event.target.value);
              this.updateQuantity(quantity);
              this.updateItemRows(quantity);
              return;
            }

            if (event.target.matches('[data-bedly-bundle-option]')) {
              const row = event.target.closest('[data-bedly-bundle-item]');
              if (row?.dataset.bedlyBundleItem === '1') this.updateProductVariantFromFirstRow();
              this.clearError();
              this.updateBundleSummary();
            }
          });

          const selected = this.querySelector('[data-bedly-bundle-quantity]:checked');
          if (selected) {
            const quantity = Number(selected.value);
            this.updateQuantity(quantity);
            this.updateItemRows(quantity);
          }

          if (this.dataset.multiVariant === 'true' && this.form) {
            this.form.addEventListener('submit', this.handleFormSubmitBound, true);
            document.addEventListener('click', this.handleBuyNowClickBound, true);
            this.ensureBundleSummaryInput();
            this.updateProductVariantFromFirstRow();
            this.updateBundleSummary();

            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                if (event.data?.sectionId !== this.dataset.sectionId || !event.data.variant) return;
                this.syncFirstRow(event.data.variant);
              });
            }
          }
        });
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleFormSubmitBound, true);
        document.removeEventListener('click', this.handleBuyNowClickBound, true);
        this.variantChangeUnsubscriber?.();
        delete this.dataset.initialized;
      }

      updateQuantity(quantity) {
        if (!this.quantityInput || !Number.isFinite(quantity)) return;

        const max = Number(this.quantityInput.max || Infinity);
        const min = Number(this.quantityInput.min || 1);
        const nextQuantity = Math.min(Math.max(quantity, min), max);

        this.quantityInput.value = nextQuantity;
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateItemRows(quantity) {
        if (this.dataset.multiVariant !== 'true') return;

        this.itemRows.forEach((row, index) => {
          const isHidden = index >= quantity;
          row.hidden = isHidden;
          row.querySelectorAll('select').forEach((select) => {
            select.disabled = isHidden;
          });
        });

        this.clearError();
        this.updateProductVariantFromFirstRow();
        this.updateBundleSummary();
      }

      parseVariants() {
        const data = this.querySelector('[data-bedly-bundle-variants]');
        if (!data) return [];

        try {
          return JSON.parse(data.textContent);
        } catch (error) {
          console.error('Bedly bundle variants could not be loaded.', error);
          return [];
        }
      }

      getVisibleRows() {
        return this.itemRows.filter((row) => !row.hidden);
      }

      getVariantForRow(row) {
        if (!row || !this.variants.length) return null;

        const optionCount = this.variants[0]?.options?.length || 0;
        const selectedOptions = new Array(optionCount);

        row.querySelectorAll('[data-bedly-bundle-option]').forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          selectedOptions[position] = select.value;
        });

        return (
          this.variants.find((variant) =>
            variant.options.every((option, index) => option === selectedOptions[index])
          ) || null
        );
      }

      getSelectedVariants() {
        return this.getVisibleRows().map((row) => this.getVariantForRow(row));
      }

      updateProductVariantFromFirstRow() {
        if (!this.form) return;

        const firstVariant = this.getVariantForRow(this.itemRows[0]);
        const variantIdInput = this.form.querySelector('[name="id"]');
        if (!firstVariant || !variantIdInput) return;

        variantIdInput.value = firstVariant.id;
        variantIdInput.disabled = !firstVariant.available;
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncNativeVariantPicker(firstVariant);
      }

      syncNativeVariantPicker(variant) {
        const variantPicker = this.productInfo?.querySelector('variant-selects');
        if (!variantPicker || !Array.isArray(variant.options)) return;

        const radios = Array.from(variantPicker.querySelectorAll('input[type="radio"]'));
        const selects = Array.from(variantPicker.querySelectorAll('select'));
        let changedControl = null;

        variant.options.forEach((optionValue, index) => {
          const position = index + 1;
          const radio = radios.find(
            (input) => input.name.endsWith(`-${position}`) && input.value === optionValue
          );

          if (radio) {
            if (!radio.checked) {
              radio.checked = true;
              changedControl = radio;
            }
            return;
          }

          const select = selects[index];
          if (select && select.value !== optionValue) {
            select.value = optionValue;
            changedControl = select;
          }
        });

        changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncFirstRow(variant) {
        const firstRow = this.itemRows[0];
        if (!firstRow || !Array.isArray(variant.options)) return;

        firstRow.querySelectorAll('[data-bedly-bundle-option]').forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          if (variant.options[position] != null) select.value = variant.options[position];
        });

        this.updateBundleSummary();
      }

      ensureBundleSummaryInput() {
        if (!this.form) return;

        this.bundleSummaryInput = this.form.querySelector('[data-bedly-bundle-summary]');
        if (this.bundleSummaryInput) return;

        this.bundleSummaryInput = document.createElement('input');
        this.bundleSummaryInput.type = 'hidden';
        this.bundleSummaryInput.name = 'properties[Odabrane pidžame]';
        this.bundleSummaryInput.dataset.bedlyBundleSummary = '';
        this.form.appendChild(this.bundleSummaryInput);
      }

      updateBundleSummary() {
        if (!this.bundleSummaryInput) return;

        this.bundleSummaryInput.value = this.getVisibleRows()
          .map((row, index) => {
            const model = row.querySelector('[data-option-name="model"]')?.value;
            const size = row.querySelector('[data-option-name="size"]')?.value;
            if (!model || !size) return null;
            return `${index + 1}. ${model}, veličina ${size}`;
          })
          .filter(Boolean)
          .join(' | ');
      }

      handleFormSubmit(event) {
        if (this.dataset.multiVariant !== 'true' || event.target !== this.form) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.addSelectedVariants();
      }

      handleBuyNowClick(event) {
        if (this.dataset.multiVariant !== 'true') return;

        const buyNowButton = event.target.closest?.('#rsi_buy_now_button');
        if (!buyNowButton || !this.productInfo?.contains(buyNowButton)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.addSelectedVariants({ openCodForm: true, buyNowButton });
      }

      async addSelectedVariants({ openCodForm = false, buyNowButton = null } = {}) {
        if (this.isSubmitting) return;

        const selectedVariants = this.getSelectedVariants();
        if (!selectedVariants.length || selectedVariants.some((variant) => !variant || !variant.available)) {
          this.showError('Odabrana kombinacija trenutno nije dostupna. Molimo odaberite drugi model ili veličinu.');
          return;
        }

        const groupedItems = new Map();
        selectedVariants.forEach((variant) => {
          groupedItems.set(variant.id, (groupedItems.get(variant.id) || 0) + 1);
        });

        const items = Array.from(groupedItems, ([id, quantity]) => ({ id, quantity }));
        const payload = { items };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        if (openCodForm) {
          this.setBuyNowLoading(buyNowButton, true);
        } else {
          this.setLoading(true);
        }
        this.clearError();

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(payload);

          const response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());
          if (response.status) throw new Error(response.description || response.message);

          const firstItem = response.items?.[0];
          if (firstItem) {
            response.id = response.id || firstItem.id;
            response.key = response.key || firstItem.key;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: firstItem?.id || items[0].id,
              cartData: response,
            });
          }

          this.cart.renderContents(response);

          if (openCodForm) {
            await this.openReleasitCartForm();
          }
        } catch (error) {
          console.error(error);
          this.showError(error.message || 'Došlo je do greške. Pokušajte ponovo.');
        } finally {
          if (openCodForm) {
            this.setBuyNowLoading(buyNowButton, false);
          } else {
            this.setLoading(false);
          }
        }
      }

      waitForReleasitRuntime(timeout = 6000) {
        return new Promise((resolve) => {
          const startedAt = Date.now();
          const checkRuntime = () => {
            const runtime = window._rsiV2;
            if (runtime?.getState && runtime?.setState && runtime?.cartService) {
              resolve(runtime);
              return;
            }

            if (Date.now() - startedAt >= timeout) {
              resolve(null);
              return;
            }

            window.setTimeout(checkRuntime, 100);
          };
          checkRuntime();
        });
      }

      async openReleasitCartForm() {
        const runtime = await this.waitForReleasitRuntime();
        if (!runtime) {
          throw new Error('Brza narudžba se nije učitala. Molimo pokušajte ponovo.');
        }

        const state = runtime.getState();
        let cartId = state.cartInfo?.id;
        if (!cartId) {
          const initialCart = await runtime.cartService.getCart('');
          cartId = initialCart?.data?.cart?.id;
        }
        if (!cartId) throw new Error('Brza narudžba nije mogla pripremiti korpu.');

        const shopifyCart = await fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
        const lines = (shopifyCart.items || []).map((item) => {
          const line = {
            merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
            quantity: item.quantity || 1,
          };
          const properties = Object.entries(item.properties || {});
          if (properties.length) {
            line.attributes = properties.map(([key, value]) => ({ key, value: String(value) }));
          }
          return line;
        });

        await runtime.cartService.clearCart(cartId);
        const result = lines.length ? await runtime.cartService.addProduct(cartId, lines) : null;
        const cartInfo = result?.cartLinesAdd?.cart || result?.cart;
        if (!cartInfo) throw new Error('Brza narudžba nije mogla učitati odabrane proizvode.');

        const products = cartInfo.lines?.edges?.map((line) => line.node.merchandise) || [];
        const firstProduct = products[0];
        await runtime.setState({
          cartInfo,
          products,
          currentVariantId: Number(firstProduct?.id?.split('/').pop()) || undefined,
          currentProductId: Number(firstProduct?.product?.id?.split('/').pop()) || undefined,
          triggerCartUpdate: 0,
          isOpen: true,
          isCartButtonLoading: false,
          isClearingCart: false,
        });
      }

      setBuyNowLoading(button, isLoading) {
        if (!button) return;

        button.toggleAttribute('disabled', isLoading);
        button.setAttribute('aria-busy', String(isLoading));
      }

      setLoading(isLoading) {
        this.isSubmitting = isLoading;
        const submitButton = this.productForm?.querySelector('[type="submit"]');
        const spinner = submitButton?.querySelector('.loading__spinner');
        if (!submitButton) return;

        submitButton.toggleAttribute('aria-disabled', isLoading);
        submitButton.classList.toggle('loading', isLoading);
        spinner?.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        if (this.errorElement) {
          this.errorElement.textContent = message;
          this.errorElement.hidden = false;
        }
        this.productForm?.handleErrorMessage?.(message);
      }

      clearError() {
        if (this.errorElement) {
          this.errorElement.textContent = '';
          this.errorElement.hidden = true;
        }
        this.productForm?.handleErrorMessage?.();
      }
    }
  );
}

document.addEventListener('click', (event) => {
  const showMoreButton = event.target.closest('[data-bedly-review-more]');
  if (showMoreButton) {
    const section = showMoreButton.closest('.bedly-product-reviews');
    if (!section) return;

    section.dataset.reviewExpanded = 'true';
    updateBedlyReviewLimit(section);
    return;
  }

  const removeMediaButton = event.target.closest('[data-bedly-review-media-remove]');
  if (removeMediaButton) {
    const field = removeMediaButton.closest('.bedly-review-form__field--upload');
    const input = field?.querySelector('[data-bedly-review-media-input]');
    if (input) input.value = '';
    updateBedlyReviewMediaPreview(input);
    return;
  }

  const filterButton = event.target.closest('[data-bedly-review-filter]');
  if (filterButton) {
    const section = filterButton.closest('.bedly-product-reviews');
    if (!section) return;

    const filter = filterButton.dataset.bedlyReviewFilter;
    const cards = section.querySelectorAll('[data-bedly-review-card]');

    cards.forEach((card) => {
      const media = card.dataset.reviewMedia;
      const rating = Number(card.dataset.reviewRating);
      const isVisible =
        filter === 'all' ||
        (filter === 'media' && media !== 'none') ||
        (filter === 'video' && media === 'video') ||
        (filter === 'five' && rating === 5);

      card.dataset.filterVisible = String(isVisible);
      card.hidden = !isVisible;
    });

    section.querySelectorAll('[data-bedly-review-filter]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === filterButton));
    });
    section.dataset.reviewExpanded = 'false';
    updateBedlyReviewLimit(section);
    filterButton.closest('[data-bedly-review-filter-menu]')?.removeAttribute('open');
    return;
  }

  const openButton = event.target.closest('[data-bedly-review-open]');
  if (openButton) {
    const dialog = document.getElementById(openButton.dataset.bedlyReviewOpen);
    if (!dialog || typeof dialog.showModal !== 'function') return;

    muteBedlyReviewVideos(dialog);
    dialog.showModal();
    openButton.setAttribute('aria-expanded', 'true');
    dialog.addEventListener(
      'close',
      () => {
        openButton.setAttribute('aria-expanded', 'false');
        openButton.focus({ preventScroll: true });
      },
      { once: true }
    );
    return;
  }

  const closeButton = event.target.closest('[data-bedly-review-close]');
  if (closeButton) {
    const dialog = closeButton.closest('dialog');
    dialog?.querySelectorAll('video').forEach((video) => video.pause());
    dialog?.close();
    return;
  }

  if (event.target.matches('.bedly-review-dialog')) {
    event.target.querySelectorAll('video').forEach((video) => video.pause());
    event.target.close();
  }
});

const bedlyReviewMobileQuery = window.matchMedia('(max-width: 749px)');

function updateBedlyReviewLimit(section) {
  if (!section) return;

  const cards = Array.from(section.querySelectorAll('[data-bedly-review-card]'));
  const showMoreButton = section.querySelector('[data-bedly-review-more]');
  const visibleCards = cards.filter((card) => card.dataset.filterVisible !== 'false');
  const isExpanded = section.dataset.reviewExpanded === 'true';

  cards.forEach((card) => card.classList.remove('bedly-review-card--mobile-truncated'));

  if (!bedlyReviewMobileQuery.matches || isExpanded || visibleCards.length <= 8) {
    if (showMoreButton) showMoreButton.hidden = true;
    return;
  }

  visibleCards.slice(8).forEach((card) => card.classList.add('bedly-review-card--mobile-truncated'));
  if (showMoreButton) showMoreButton.hidden = false;
}

function updateBedlyReviewMediaPreview(input) {
  if (!input) return;

  const field = input.closest('.bedly-review-form__field--upload');
  const preview = field?.querySelector('[data-bedly-review-media-preview]');
  const image = field?.querySelector('[data-bedly-review-media-image]');
  const name = field?.querySelector('[data-bedly-review-media-name]');
  const error = field?.querySelector('[data-bedly-review-media-error]');
  const file = input.files?.[0];

  if (error) {
    error.hidden = true;
    error.textContent = '';
  }

  if (!file) {
    if (preview) preview.hidden = true;
    if (image?.dataset.objectUrl) URL.revokeObjectURL(image.dataset.objectUrl);
    if (image) {
      image.removeAttribute('src');
      delete image.dataset.objectUrl;
    }
    return;
  }

  if (!file.type.startsWith('image/')) {
    input.value = '';
    if (error) {
      error.textContent = 'Odaberite fotografiju u podržanom formatu.';
      error.hidden = false;
    }
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    input.value = '';
    if (error) {
      error.textContent = 'Fotografija može imati najviše 10 MB.';
      error.hidden = false;
    }
    return;
  }

  if (image?.dataset.objectUrl) URL.revokeObjectURL(image.dataset.objectUrl);
  const objectUrl = URL.createObjectURL(file);
  if (image) {
    image.src = objectUrl;
    image.dataset.objectUrl = objectUrl;
  }
  if (name) name.textContent = file.name;
  if (preview) preview.hidden = false;
}

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-bedly-review-media-input]')) {
    updateBedlyReviewMediaPreview(event.target);
  }
});

function initializeBedlyReviewSections() {
  document.querySelectorAll('.bedly-product-reviews').forEach((section) => {
    section.querySelectorAll('[data-bedly-review-card]').forEach((card) => {
      if (!card.dataset.filterVisible) card.dataset.filterVisible = 'true';
    });
    if (!section.dataset.reviewExpanded) section.dataset.reviewExpanded = 'false';
    muteBedlyReviewVideos(section);
    updateBedlyReviewLimit(section);
  });
}

function muteBedlyReviewVideos(root = document) {
  root.querySelectorAll('.bedly-review-dialog__video').forEach((video) => {
    video.defaultMuted = true;
    video.muted = true;
    video.volume = 0;

    if (video.dataset.bedlyMuteLocked === 'true') return;
    video.dataset.bedlyMuteLocked = 'true';
    video.addEventListener('volumechange', () => {
      if (!video.muted || video.volume !== 0) {
        video.muted = true;
        video.volume = 0;
      }
    });
  });
}

initializeBedlyReviewSections();
bedlyReviewMobileQuery.addEventListener('change', initializeBedlyReviewSections);
document.addEventListener('shopify:section:load', initializeBedlyReviewSections);
